import {TextField, TextFieldProps, Tooltip} from "@mui/material";
import {FieldPath, FieldPathValue, useController, UseControllerProps} from "react-hook-form";
import React, {useState} from "react";

export interface FormTextFieldProps<
	TFieldValues extends object,
	TFieldName extends FieldPath<TFieldValues>,
> extends Omit<TextFieldProps, "value" | "onChange" | "ref"> {
	control: UseControllerProps<TFieldValues, TFieldName>["control"];
	name: UseControllerProps<TFieldValues, TFieldName>["name"];
	rules?: UseControllerProps<TFieldValues, TFieldName>["rules"],
	tooltip?: React.ReactNode
}

export const FormTextField = <TFieldValues extends object, TFieldName extends FieldPath<TFieldValues>>(
	{
		control,
		name,
		rules,
		tooltip,
		...other
	}: FormTextFieldProps<TFieldValues, TFieldName>
) => {
	const { field, fieldState } = useController({
		name,
		control,
		rules,
		defaultValue: "" as FieldPathValue<TFieldValues, TFieldName>
	});

	const [tooltipOpen, setTooltipOpen] = useState(false);

	const transformedField = {
		...field,
		value: field.value !== null ? field.value : ""
	}

	return (
		<Tooltip title={tooltip} followCursor open={tooltipOpen}
				 onOpen={() => {
					 setTooltipOpen(true);
				 }}
				 onClose={() => {
					 setTooltipOpen(false);
				 }}>
			<TextField
				onFocus={() => {
					setTooltipOpen(false);
				}}
				onInput={() => {
					setTooltipOpen(false);
				}}
				error={fieldState.error !== undefined}
				helperText={fieldState.error?.message}
				{...transformedField}
				{...other}
			/>
		</Tooltip>
	);
};
